import {Badge} from "../../../components/Badge";
import {
    Link,
    useNavigate,
    useSearchParams,
} from "react-router-dom";
import {organizationStore, useOrganizationStore} from "../../../store/organization";
import {createApolloClient} from "../../../providers/ApolloClientFactory";
import {TICKETS_LIST_QUERY, TICKETS_LIST_QUERY_ALL, TICKET_SEARCH_FILTER_OPTIONS} from "./queries";
import {useEffect, useMemo, useRef, useState} from "react";
import {eventStore, useEventStore} from "../../../store/event";
import {usePermissions} from "../../auth/api";
import {
    ColDef,
    GridApi,
    SizeColumnsToContentStrategy,
} from "ag-grid-community";
import {Breadcrumbs} from "../../../components/Breadcrumbs";
import {Input} from "../../../components/inputs/Input";
import {PrimaryButton, SecondaryButton} from "../../../components/Button";
import CheckboxDropdown from "../../../components/CheckboxDropdown";
import {AgGridReact} from "ag-grid-react";
import moment from "moment/moment";
import {userStore} from "../../../store/user";
import {Tickets} from "../../../types/tickets";
import {
    EMPTY_TICKET_FILTERS,
    FilterOption,
    TicketSearchFilters,
    buildFilterWhere,
    buildIdNameOptions,
    buildModifierGroupOptions,
    buildPrintHtml,
    buildTicketNameOptions,
    hasActiveFilters,
    normalizeSelection,
    orderMatchesFilters,
    ticketDetailLines,
    ticketQuantity,
} from "./ticketSearchFilters";

const client = createApolloClient();
const {getState} = organizationStore;
const {getState: getEventStore} = eventStore;
const {getState: getUserStore} = userStore;

const buildSearch = (search: string) => [
    {
        attendee: {
            first_name: {
                _ilike: `%${search}%`,
            },
        }
    },
    {
        attendee: {
            last_name: {
                _ilike: `%${search}%`,
            },
        }
    },
    {
        attendee: {
            phone_number: {
                _ilike: `%${search}%`,
            },
        }
    },
    {
        attendee: {
            business_name: {
                _ilike: `%${search}%`,
            },
        }
    },
    {
        email: {
            _ilike: `%${search}%`,
        },
    },
    {
        reference_id: {
            _ilike: `%${search}%`,
        },
    },
];

/** Which orders this user may see: organization, event, or their assigned events. */
const buildScopeWhere = (): any => {
    const rawOrganizationId = getState().organizationId;
    const orgId = parseInt(rawOrganizationId !== null ? rawOrganizationId.toString() : '', 10);
    const evtId = getEventStore().eventId;
    const user = getUserStore().user;

    let where: any = {};
    if (orgId && orgId !== 0) {
        where = {...where, event: {organization_id: {_eq: orgId}}};
    }
    if (evtId) {
        where = {...where, event_id: {_eq: evtId}};
    } else if (user?.events) {
        where = {...where, event_id: {_in: user?.events}};
    }
    return where;
};

/** Same scope, for the ticketing item definitions behind the filter options. */
const buildItemScopeWhere = (): any => {
    const rawOrganizationId = getState().organizationId;
    const orgId = parseInt(rawOrganizationId !== null ? rawOrganizationId.toString() : '', 10);
    const evtId = getEventStore().eventId;
    const user = getUserStore().user;

    if (evtId) return {event_id: {_eq: evtId}};
    let where: any = {};
    if (orgId && orgId !== 0) where = {...where, event: {organization_id: {_eq: orgId}}};
    if (user?.events) where = {...where, event_id: {_in: user?.events}};
    return where;
};

/** Search box + RP-2540 filters + scope, as one Hasura where clause. */
const buildWhere = (search: string | null, filters: TicketSearchFilters): any => {
    let where: any = {_or: search ? buildSearch(search) : undefined, ...buildScopeWhere()};
    const filterWhere = buildFilterWhere(filters);
    if (filterWhere.length) where = {...where, _and: filterWhere};
    return where;
};

// Filtered results shown on screen. A filter like "the event's only vendor"
// can match every order of a big event (Wurstfest has 50k+), so the page
// stops here and says so; Export All still gets everything.
const FILTERED_ROW_CAP = 5000;

const formatDate = (value?: string | null) =>
    value ? moment(value).format('ddd, MMM DD, YYYY h:mm a') : '';

// Aggregate token balances by token name ("Tickets Purchased").
const getTicketsPurchased = (items: any): string => {
    if (!items || !Array.isArray(items) || items.length === 0) {
        return '';
    }
    const tokenBalances: Record<string, number> = {};
    items.forEach((item: any) => {
        const tokenPayload = item?.token_payload;
        if (tokenPayload && typeof tokenPayload === 'object') {
            Object.values<any>(tokenPayload).forEach((token) => {
                const tokenName = token?.redeemable_token_name;
                const balance = token?.balance || 0;
                if (tokenName) {
                    tokenBalances[tokenName] = (tokenBalances[tokenName] || 0) + balance;
                }
            });
        }
    });
    return Object.entries(tokenBalances)
        .map(([name, balance]) => `${name} - ${balance}`)
        .join(', ');
};

type CellRenderer = (params: {
    value:
        | string
        | number
        | boolean
        | React.ReactElement<any, string | React.JSXElementConstructor<any>>
        | React.ReactFragment
        | React.ReactPortal
        | null
        | undefined;
    data: Tickets;
}) => JSX.Element | null;
const createCellRenderer = (
    permission: boolean,
    linkFunc: (params: { data: Tickets }) => string
): CellRenderer => {
    return (params) => {
        if (params.value === undefined) {
            return null;
        }

        return (
            <>
                {permission ? (
                    <Link to={linkFunc(params)}>
                        <Badge>{params.value}</Badge>
                    </Link>
                ) : (
                    <Badge>{params.value}</Badge>
                )}
            </>
        );
    };
};

/**
 * One definition per column, used by the grid, the column picker, Print and
 * Export All, so the four never drift apart. `text` is the plain value.
 */
interface ColumnSpec {
    id: string;
    header: string;
    text: (t: Tickets, f: TicketSearchFilters) => string;
    /** shown by default */
    visible: boolean;
    extra?: Partial<ColDef<Tickets>>;
}

export const TicketList = () => {
    const {editCreatePermission, SearchPermission} =
        usePermissions("ticketingReporting");
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const gridRef = useRef<GridApi>();
    const [rowData, setRowData] = useState<Tickets[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [exportingAll, setExportingAll] = useState<boolean>(false);
    const [searchValue, setSearchValue] = useState<string>("");
    const debounceTimer = useRef<NodeJS.Timeout | null>(null);
    const organizationId = useOrganizationStore((s) => s.organizationId);
    const eventId = useEventStore((s) => s.eventId);

    // RP-2540 filters
    const [filters, setFilters] = useState<TicketSearchFilters>(EMPTY_TICKET_FILTERS);
    const [vendorOptions, setVendorOptions] = useState<FilterOption[]>([]);
    const [locationOptions, setLocationOptions] = useState<FilterOption[]>([]);
    const [itemDefs, setItemDefs] = useState<any[]>([]);
    const [hiddenColumns, setHiddenColumns] = useState<string[] | null>(null);
    const [capped, setCapped] = useState<boolean>(false);
    const filtersActive = hasActiveFilters(filters);

    // RP-2540: the date inputs edit a draft. The browser's date picker changes
    // the value while you move between months, so applying on every change made
    // the results jump around. Dates only apply on "Apply dates" or Enter.
    const [dateDraft, setDateDraft] = useState({from: filters.dateFrom, to: filters.dateTo});
    useEffect(() => {
        // Keep the draft in step when the applied dates change elsewhere
        // (Clear filters, switching events).
        setDateDraft({from: filters.dateFrom, to: filters.dateTo});
    }, [filters.dateFrom, filters.dateTo]);
    const datesPending = dateDraft.from !== filters.dateFrom || dateDraft.to !== filters.dateTo;
    const applyDates = () => {
        if (datesPending) setFilters({...filters, dateFrom: dateDraft.from, dateTo: dateDraft.to});
    };
    const clearFilters = () => {
        setFilters(EMPTY_TICKET_FILTERS);
        setDateDraft({from: "", to: ""});
    };

    // Initialize search value from URL params
    useEffect(() => {
        const urlSearch = searchParams.get("search");
        if (urlSearch) {
            setSearchValue(urlSearch);
        }
    }, []);

    // Handle debounced search
    const handleSearchChange = (value: string | number | undefined) => {
        const searchString = typeof value === "string" ? value : "";
        setSearchValue(searchString);

        // Clear existing timer
        if (debounceTimer.current) {
            clearTimeout(debounceTimer.current);
        }

        // Set new timer
        debounceTimer.current = setTimeout(() => {
            if (searchString && searchString.length > 0) {
                navigate(`/tickets/list?search=${searchString}`);
            } else {
                navigate(`/tickets/list`);
            }
        }, 500); // 500ms debounce
    };

    // Cleanup timer on unmount
    useEffect(() => {
        return () => {
            if (debounceTimer.current) {
                clearTimeout(debounceTimer.current);
            }
        };
    }, []);

    // Filter options follow the selected organization / event. A new event
    // means new vendors, tickets and modifiers, so the old picks are cleared.
    useEffect(() => {
        clearFilters();
        const loadOptions = async () => {
            try {
                const {data} = await client.query({
                    query: TICKET_SEARCH_FILTER_OPTIONS,
                    variables: {orderWhere: buildScopeWhere(), itemWhere: buildItemScopeWhere()},
                    fetchPolicy: 'network-only',
                });
                setVendorOptions(buildIdNameOptions(data?.vendors || [], "vendor_id", "vendor"));
                setLocationOptions(buildIdNameOptions(data?.locations || [], "location_id", "location"));
                setItemDefs(data?.items_ticketing || []);
            } catch (error) {
                console.error("Error loading ticket search filter options:", error);
                setVendorOptions([]);
                setLocationOptions([]);
                setItemDefs([]);
            }
        };
        loadOptions();
    }, [organizationId, eventId]);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const search = searchParams.get("search");
                const where = buildWhere(search, filters);
                // Searching queries all matching records, filtering up to
                // FILTERED_ROW_CAP. Otherwise, limit to the 1000 most recent.
                const limit = search ? null : filtersActive ? FILTERED_ROW_CAP : 1000;

                const {data} = await client.query({
                    query: TICKETS_LIST_QUERY,
                    variables: {
                        where,
                        limit,
                    },
                });
                const rows: Tickets[] = data.orders_ticketing || [];
                setCapped(!search && filtersActive && rows.length >= FILTERED_ROW_CAP);
                // Line-by-line check: a ticket name and its modifier picks
                // must be on the SAME line of the order.
                setRowData(filtersActive ? rows.filter((o) => orderMatchesFilters(o, filters)) : rows);
            } catch (error) {
                console.error("Error fetching tickets:", error);
                setRowData([]);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [searchParams, organizationId, eventId, filters]);

    const ticketNameOptions = useMemo(
        () => buildTicketNameOptions(itemDefs, rowData),
        [itemDefs, rowData]
    );
    const modifierGroups = useMemo(
        () => buildModifierGroupOptions(itemDefs, rowData, filters.ticketNames),
        [itemDefs, rowData, filters.ticketNames]
    );

    const setTicketNames = (selected: string[] | null) => {
        const ticketNames = normalizeSelection(selected, ticketNameOptions);
        // Drop modifier picks for groups the chosen tickets don't have.
        const offered = new Set(
            buildModifierGroupOptions(itemDefs, rowData, ticketNames).map((g) => g.name)
        );
        const modifiers = Object.fromEntries(
            Object.entries(filters.modifiers).filter(([group]) => offered.has(group))
        );
        setFilters({...filters, ticketNames, modifiers});
    };

    const columns: ColumnSpec[] = [
        {id: "date", header: "Date", visible: true, text: (t) => formatDate(t.created_at)},
        {id: "order_id", header: "Order ID", visible: true, text: (t) => String(t.id ?? '')},
        {id: "reference_id", header: "Reference ID", visible: true, text: (t) => t.reference_id || ''},
        {id: "first_name", header: "Attendee First Name", visible: true, text: (t) => t.attendee?.first_name || ''},
        {id: "last_name", header: "Attendee Last Name", visible: true, text: (t) => t.attendee?.last_name || ''},
        {
            id: "ticket_details",
            header: "Ticket Details",
            visible: true,
            text: (t, f) => ticketDetailLines(t.items, f).join("\n"),
            extra: {
                autoHeight: true,
                wrapText: true,
                cellRenderer: (params: any) => (
                    <div style={{lineHeight: "1.4", padding: "6px 0"}}>
                        {String(params.value || "").split("\n").filter(Boolean).map((l, i) => (
                            <div key={i}>{l}</div>
                        ))}
                    </div>
                ),
            },
        },
        {id: "quantity", header: "Qty", visible: true, text: (t, f) => String(ticketQuantity(t.items, f) || '')},
        {id: "tickets_purchased", header: "Tickets Purchased", visible: true, text: (t) => getTicketsPurchased(t.items)},
        {id: "event", header: "Event", visible: true, text: (t) => t.event?.name || ''},
        {id: "vendor", header: "Vendor", visible: true, text: (t) => t.vendor?.name || ''},
        {id: "location", header: "Location", visible: true, text: (t) => t.location?.name || ''},
        {id: "business_name", header: "Business Name", visible: true, text: (t) => t.attendee?.business_name || ''},
        {id: "email", header: "Attendee Email", visible: true, text: (t) => t.email || ''},
        {id: "phone", header: "Attendee Phone Number", visible: true, text: (t) => t.attendee?.phone_number || ''},
        {id: "attendee_id", header: "Attendee ID", visible: true, text: (t) => String(t.attendee?.id ?? '')},
        {id: "processor_ref", header: "Payment Processor Reference ID", visible: true, text: (t) => t.mx_ref_id || ''},
    ];
    const columnOptions: FilterOption[] = columns.map((c) => ({value: c.id, label: c.header}));
    const isVisible = (c: ColumnSpec) =>
        hiddenColumns === null ? c.visible : !hiddenColumns.includes(c.id);
    const visibleColumns = columns.filter(isVisible);

    const colDefs: ColDef<Tickets>[] = columns.map((c) => {
        const base: ColDef<Tickets> = {
            colId: c.id,
            headerName: c.header,
            hide: !isVisible(c),
            valueGetter: (p) => (p.data ? c.text(p.data, filters) : ''),
            filter: "agTextColumnFilter",
            ...c.extra,
        };
        if (c.id === "date") {
            return {
                ...base,
                // Sort by the real timestamp, show the formatted date.
                valueGetter: (p) => p.data?.created_at,
                valueFormatter: (p) => formatDate(p.value),
                filterValueGetter: (p) => formatDate(p.data?.created_at),
                pinned: 'left',
            };
        }
        if (c.id === "order_id") {
            return {
                ...base,
                valueGetter: (p) => p.data?.id,
                cellRenderer: createCellRenderer(
                    SearchPermission,
                    (p) => `/tickets/${p.data?.attendee?.id}/edit`
                ),
                pinned: "left",
            };
        }
        return base;
    });

    const defaultColDef: ColDef<Tickets> = {
        // A filter box right under each header, instead of only the menu.
        floatingFilter: true,
        sortable: true,
        resizable: true,
    };
    const rowHeight = 50;
    const autoSizeStrategy: SizeColumnsToContentStrategy = {
        type: "fitCellContents",
    };

    // Rows as currently shown: after the grid's own column filters and sort.
    const shownRows = (): Tickets[] => {
        const rows: Tickets[] = [];
        gridRef.current?.forEachNodeAfterFilterAndSort((node) => {
            if (node.data) rows.push(node.data);
        });
        return rows;
    };

    const filterSummary = (): string => {
        const label = (opts: FilterOption[], values: string[]) =>
            values.map((v) => opts.find((o) => o.value === v)?.label ?? v).join(", ");
        const parts: string[] = [];
        if (filters.vendorIds.length) parts.push(`Vendor: ${label(vendorOptions, filters.vendorIds)}`);
        if (filters.locationIds.length) parts.push(`Location: ${label(locationOptions, filters.locationIds)}`);
        if (filters.ticketNames.length) parts.push(`Ticket: ${filters.ticketNames.join(", ")}`);
        Object.entries(filters.modifiers).forEach(([group, labels]) => {
            if (labels.length) parts.push(`${group}: ${labels.join(", ")}`);
        });
        if (filters.dateFrom || filters.dateTo) {
            parts.push(`Dates: ${filters.dateFrom || "…"} to ${filters.dateTo || "…"}`);
        }
        const search = searchParams.get("search");
        if (search) parts.push(`Search: "${search}"`);
        return parts.join(" · ");
    };

    const handlePrint = () => {
        const rows = shownRows();
        const eventName = rows[0]?.event?.name || "Tickets";
        const summary = filterSummary();
        const subtitle = [
            `${rows.length} order${rows.length === 1 ? "" : "s"}`,
            summary,
            `Printed ${moment().format("MMM DD, YYYY h:mm a")}`,
        ].filter(Boolean).join(" · ");
        const html = buildPrintHtml(
            `${eventName} — Ticket List`,
            subtitle,
            visibleColumns.map((c) => c.header),
            rows.map((t) => visibleColumns.map((c) => c.text(t, filters)))
        );
        const win = window.open("", "_blank");
        if (!win) return;
        win.document.open();
        win.document.write(html);
        win.document.close();
        win.focus();
        // Give the new window a moment to lay out before the print dialog.
        setTimeout(() => win.print(), 250);
    };

    const handleExportCSV = () => {
        if (gridRef.current) {
            const timestamp = new Date().toISOString().replace(/[-:]/g, "");
            const params = {
                fileName: `Tickets_${timestamp}.csv`,
                columnSeparator: ",",
            };
            gridRef.current.exportDataAsCsv(params);
        }
    };

    const handleExportAllCSV = async () => {
        setExportingAll(true);
        try {
            const search = searchParams.get("search");
            const where = buildWhere(search, filters);

            // Fetch all data in batches to avoid timeouts
            const BATCH_SIZE = 10000;
            const allTickets: Tickets[] = [];
            let offset = 0;
            let hasMore = true;

            while (hasMore) {
                const {data} = await client.query({
                    query: TICKETS_LIST_QUERY_ALL,
                    variables: {
                        where,
                        limit: BATCH_SIZE,
                        offset,
                    },
                    fetchPolicy: 'network-only',
                });

                const batch = data.orders_ticketing || [];
                allTickets.push(...batch);

                if (batch.length < BATCH_SIZE) {
                    hasMore = false;
                } else {
                    offset += BATCH_SIZE;
                }
            }
            const exportTickets = filtersActive
                ? allTickets.filter((o) => orderMatchesFilters(o, filters))
                : allTickets;

            // Build CSV content
            const headers = [
                'Date',
                'Order ID',
                'Reference ID',
                'Attendee First Name',
                'Attendee Last Name',
                'Ticket Details',
                'Qty',
                'Tickets Purchased',
                'Transaction Amount',
                'Event',
                'Vendor',
                'Location',
                'Business Name',
                'Attendee Email',
                'Attendee Phone Number',
                'Attendee ID',
                'Attendee Address',
                'Attendee City',
                'Attendee State',
                'Attendee Zipcode',
                'Payment Processor Reference ID'
            ];

            const escapeCSV = (value: any) => {
                if (value === null || value === undefined) return '';
                const str = String(value);
                if (str.includes(',') || str.includes('"') || str.includes('\n')) {
                    return `"${str.replace(/"/g, '""')}"`;
                }
                return str;
            };

            const rows = exportTickets.map((ticket) => [
                formatDate(ticket.created_at),
                ticket.id,
                ticket.reference_id || '',
                ticket.attendee?.first_name || '',
                ticket.attendee?.last_name || '',
                ticketDetailLines(ticket.items, filters).join('\n'),
                ticketQuantity(ticket.items, filters) || '',
                getTicketsPurchased(ticket.items),
                ticket.credit_not_present_charged ? (ticket.credit_not_present_charged / 100).toFixed(2) : '',
                ticket.event?.name || '',
                ticket.vendor?.name || '',
                ticket.location?.name || '',
                ticket.attendee?.business_name || '',
                ticket.email || '',
                ticket.attendee?.phone_number || '',
                ticket.attendee?.id || '',
                [ticket.attendee?.address, ticket.attendee?.address_2].filter(Boolean).join(' '),
                ticket.attendee?.city || '',
                ticket.attendee?.state || '',
                ticket.attendee?.zipcode || '',
                ticket.mx_ref_id || ''
            ]);

            const csvContent = [
                headers.map(escapeCSV).join(','),
                ...rows.map(row => row.map(escapeCSV).join(','))
            ].join('\n');

            // Download the file
            const timestamp = new Date().toISOString().replace(/[-:]/g, "");
            const blob = new Blob([csvContent], {type: 'text/csv;charset=utf-8;'});
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = `Tickets_All_${timestamp}.csv`;
            link.style.display = 'none';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(link.href);
        } catch (error) {
            console.error("Error exporting all tickets:", error);
        } finally {
            setExportingAll(false);
        }
    };
    const gridOptions = {
        enableCellTextSelection: true,
    };

    const dateInputClass =
        "mt-1 w-full rounded border border-gray-300 bg-white px-2 py-[6px] text-sm text-gray-700 focus:outline-none focus:border-red-500 focus:ring-1 focus:ring-red-500";

    return (
        <div style={{height: "75vh"}}>
            <div
                className={"ag-theme-quartz"}
                style={{width: "100%", height: "100%", display: "flex", flexDirection: "column"}}
            >
                <div className=" grid gap-2 grid-cols-6">
                    <Breadcrumbs/>
                    <div className="col-span-2 col-start-5 mt-2">
                        <Input
                            name="search"
                            placeholder={`Search Tickets`}
                            value={searchValue}
                            onChange={handleSearchChange}
                        />
                    </div>
                    <div className="py-2 col-span-3 col-start-7 flex gap-2">
                        <SecondaryButton onClick={handlePrint}>
                            Print
                        </SecondaryButton>
                        <SecondaryButton onClick={handleExportAllCSV} isLoading={exportingAll}>
                            Export All to CSV
                        </SecondaryButton>
                        <SecondaryButton onClick={handleExportCSV}>
                            Export to CSV
                        </SecondaryButton>
                    </div>
                </div>

                {/* RP-2540: multi-select filters across the top. Empty = all. */}
                <div className="relative grid gap-3 grid-cols-2 md:grid-cols-4 xl:grid-cols-8 items-end py-3">
                    <div className="relative">
                        <CheckboxDropdown
                            title="Vendor"
                            pickMode
                            searchAbove={8}
                            options={vendorOptions}
                            selected={filters.vendorIds.length ? filters.vendorIds : null}
                            onChange={(v) => setFilters({...filters, vendorIds: normalizeSelection(v, vendorOptions)})}
                        />
                    </div>
                    <div className="relative">
                        <CheckboxDropdown
                            title="Location"
                            pickMode
                            searchAbove={8}
                            options={locationOptions}
                            selected={filters.locationIds.length ? filters.locationIds : null}
                            onChange={(v) => setFilters({...filters, locationIds: normalizeSelection(v, locationOptions)})}
                        />
                    </div>
                    <div className="relative">
                        <CheckboxDropdown
                            title="Ticket Name"
                            pickMode
                            searchAbove={8}
                            options={ticketNameOptions}
                            selected={filters.ticketNames.length ? filters.ticketNames : null}
                            onChange={setTicketNames}
                        />
                    </div>
                    {modifierGroups.map((group) => (
                        <div className="relative" key={group.name}>
                            <CheckboxDropdown
                                title={group.name}
                                pickMode
                                searchAbove={8}
                                options={group.options}
                                selected={filters.modifiers[group.name]?.length ? filters.modifiers[group.name] : null}
                                onChange={(v) =>
                                    setFilters({
                                        ...filters,
                                        modifiers: {
                                            ...filters.modifiers,
                                            [group.name]: normalizeSelection(v, group.options),
                                        },
                                    })
                                }
                            />
                        </div>
                    ))}
                    <div>
                        <label className="block text-sm font-medium text-gray-700">From</label>
                        <input
                            type="date"
                            value={dateDraft.from}
                            onChange={(e) => setDateDraft({...dateDraft, from: e.target.value})}
                            onKeyDown={(e) => e.key === "Enter" && applyDates()}
                            className={dateInputClass}
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700">To</label>
                        <input
                            type="date"
                            value={dateDraft.to}
                            onChange={(e) => setDateDraft({...dateDraft, to: e.target.value})}
                            onKeyDown={(e) => e.key === "Enter" && applyDates()}
                            className={dateInputClass}
                        />
                    </div>
                    <div>
                        {datesPending ? (
                            <PrimaryButton onClick={applyDates}>Apply dates</PrimaryButton>
                        ) : (
                            <SecondaryButton isDisabled>Apply dates</SecondaryButton>
                        )}
                    </div>
                    <div className="relative">
                        <CheckboxDropdown
                            title="Columns"
                            pickMode
                            searchAbove={8}
                            options={columnOptions}
                            selected={visibleColumns.map((c) => c.id)}
                            onChange={(v) => {
                                // pickMode: null = All. Unticking the last column also
                                // comes back as null, so the grid is never left empty.
                                const shown = v && v.length ? v : columns.map((c) => c.id);
                                setHiddenColumns(columns.map((c) => c.id).filter((id) => !shown.includes(id)));
                            }}
                        />
                    </div>
                    <div>
                        <SecondaryButton
                            onClick={clearFilters}
                            isDisabled={!filtersActive && !datesPending}
                        >
                            Clear filters
                        </SecondaryButton>
                    </div>
                </div>

                {capped && !loading && (
                    <div className="mb-2 rounded border border-yellow-300 bg-yellow-50 px-3 py-2 text-sm text-yellow-800">
                        Showing the {FILTERED_ROW_CAP.toLocaleString()} most recent matching orders. Narrow the
                        filters, or use Export All to CSV to get every match.
                    </div>
                )}
                {loading ? (
                    <div className="flex items-center justify-center h-full">
                        <div className="text-center">
                            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
                            <p className="mt-4 text-gray-600">Loading tickets...</p>
                        </div>
                    </div>
                ) : (
                    <div style={{flex: 1, minHeight: 0}}>
                        <AgGridReact
                            rowData={rowData}
                            columnDefs={colDefs}
                            defaultColDef={defaultColDef}
                            rowHeight={rowHeight}
                            autoSizeStrategy={autoSizeStrategy}
                            pagination={true}
                            gridOptions={gridOptions}
                            paginationPageSize={100}
                            onGridReady={(params) => (gridRef.current = params.api)}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};
